/**
 * Voyage Data Composable
 * Handles data fetching for voyage details, summary, and telemetry data
 */

import axios from 'axios';
import VoyageSummarySvc from '@/services/VoyageSummarySvc';
import SummarySvc from '@/services/SummarySvc';
import moment from 'moment';

export function useVoyageData() {
  // Bucket HARUS habis membagi 24 jam biar dilayani cache backend: HOUR×1 buat
  // voyage pendek, DAY×1 buat yang panjang (±titik chart tetep wajar). JANGAN
  // hitung aggregatedLength dinamis dari durasi — bucket kayak HOUR×43 bypass
  // cache & jatuh ke query lama yang berat.
  const getTelemetryAggregationParams = (voyage) => {
    const start = moment(voyage?.mvygStartTime);
    const end = moment(voyage?.mvygEndTime);
    const hours = start.isValid() && end.isValid()
      ? Math.max((end.unix() - start.unix()) / 3600, 0)
      : 0;

    return {
      aggregatedUnit: hours > 7 * 24 ? 'DAY' : 'HOUR',
      aggregatedLength: 1,
    };
  };

  // Satu request per tipe device untuk SELURUH rentang voyage — backend
  // meng-cache per hari, jadi mecah-mecah rentang malah bikin lambat.
  const fetchTelemetryByType = async (voyage, devcType) => {
    if (!voyage || !voyage.mvygMassId) {
      return null;
    }

    const response = await SummarySvc.getSummary({
      devcType: [devcType],
      devcMassId: voyage.mvygMassId,
      start: moment(voyage.mvygStartTime).unix(),
      end: moment(voyage.mvygEndTime).unix(),
      ...getTelemetryAggregationParams(voyage),
      showDetails: true,
    });

    return response.data?.data?.[devcType] || response.data?.[devcType] || response.data || null;
  };

  /**
   * Fetch computed voyage summary metrics by ID
   * @param {string} voyageId - Voyage ID
   * @returns {Promise<Object|null>} Summary data or null
   */
  const fetchSummary = async (voyageId) => {
    try {
      const response = await VoyageSummarySvc.fetchById(voyageId);
      return response?.summary || null;
    } catch (error) {
      // Error diterusin apa adanya biar pesan asli backend nyampe ke toast.
      if (!axios.isCancel(error)) {
        console.error('fetchSummary error:', error);
      }
      throw error;
    }
  };

  /**
   * Fetch GPS data for a voyage
   * @param {Object} voyage - Voyage object with mvygMassId, mvygStartTime, mvygEndTime
   * @returns {Promise<Object|null>} GPS data or null
   */
  const fetchGpsData = async (voyage) => {
    try {
      return await fetchTelemetryByType(voyage, 'gps');
    } catch (error) {
      console.error('fetchGpsData error:', error);
      return null;
    }
  };

  /**
   * Fetch RPM data for a voyage
   * @param {Object} voyage - Voyage object with mvygMassId, mvygStartTime, mvygEndTime
   * @returns {Promise<Object|null>} RPM data or null
   */
  const fetchRpmData = async (voyage) => {
    try {
      return await fetchTelemetryByType(voyage, 'rpm');
    } catch (error) {
      console.error('fetchRpmData error:', error);
      return null;
    }
  };

  /**
   * Fetch flowmeter (fuel consumption) data for a voyage
   * @param {Object} voyage - Voyage object with mvygMassId, mvygStartTime, mvygEndTime
   * @returns {Promise<Object|null>} Flowmeter data or null
   */
  const fetchFlowmeterData = async (voyage) => {
    try {
      return await fetchTelemetryByType(voyage, 'flowmeter');
    } catch (error) {
      console.error('fetchFlowmeterData error:', error);
      return null;
    }
  };

  // Trail peta dari endpoint khusus /summary/trail (payload jauh lebih kecil
  // dari gps summary). Hasil dikonversi ke shape gpsData lama
  // ({ data: { [ts]: {latitude, longitude, ...} }, lastTimestamp }) biar
  // VoyageMap & mapIcons gak perlu berubah. Null kalau gagal (mis. belum punya
  // permission Summary_Trail) — pemanggil fallback ke gps summary.
  const fetchTrailData = async (voyage) => {
    if (!voyage || !voyage.mvygMassId) {
      return null;
    }

    try {
      const response = await SummarySvc.getTrail({
        devcMassId: voyage.mvygMassId,
        start: moment(voyage.mvygStartTime).unix(),
        end: moment(voyage.mvygEndTime).unix(),
      });

      const trail = response.data?.data;
      if (!trail?.trail?.length || !trail?.fields?.length) {
        return null;
      }

      const data = {};
      let lastTimestamp = 0;
      trail.trail.forEach((point) => {
        const entry = {};
        trail.fields.forEach((field, index) => {
          entry[field] = point[index];
        });
        if (entry.timestamp) {
          data[entry.timestamp] = entry;
          lastTimestamp = Math.max(lastTimestamp, entry.timestamp);
        }
      });

      return { data, lastTimestamp };
    } catch (error) {
      console.error('fetchTrailData error:', error);
      return null;
    }
  };

  /**
   * Load all voyage data (convenience method)
   * @param {Object} context - Context object with voyageId
   * @returns {Promise<Object>} Object with all loaded data
   */
  const loadAllVoyageData = async ({ voyageId }) => {
    const summary = await fetchSummary(voyageId);
    const voyage = summary?.master || null;

    return {
      voyage, summary,
    };
  };

  /**
   * Load telemetry data (GPS, RPM, Flowmeter, map trail) for a voyage
   * @param {Object} voyage - Voyage object
   * @returns {Promise<Object>} Object with gpsData, rpmData, flowmeterData, trailData
   */
  const loadTelemetryData = async (voyage) => {
    const [gpsData, rpmData, flowmeterData, trailData] = await Promise.all([
      fetchGpsData(voyage),
      fetchRpmData(voyage),
      fetchFlowmeterData(voyage),
      fetchTrailData(voyage),
    ]);

    return {
      gpsData, rpmData, flowmeterData, trailData,
    };
  };

  return {
    // Individual fetch methods
    fetchSummary,
    fetchGpsData,
    fetchRpmData,
    fetchFlowmeterData,
    fetchTrailData,
    getTelemetryAggregationParams,
    // Convenience methods
    loadAllVoyageData,
    loadTelemetryData,
  };
}

export default useVoyageData;
